/**
 * "Send by email" dialog for the print window — recipients/subject/body plus
 * a read-only recap of what will actually be sent: the current attachment
 * selection (merged PDF + any non-mergeable files as separate attachments).
 */
import React, { useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { printFormsApi } from '@/utils/api'
import { printErrorMessage } from './printErrors'
import type { FilledSpreadsheetModel } from './spreadsheet/types'
import type { Attachment } from '../AttachmentPanel'

export interface EmailPrintDialogProps {
  open: boolean
  onClose: () => void
  connectionId: string
  model: FilledSpreadsheetModel
  mergeableSelected: Attachment[]
  otherSelected: Attachment[]
}

function parseRecipients(raw: string): string[] {
  return raw
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean)
}

export function EmailPrintDialog({
  open,
  onClose,
  connectionId,
  model,
  mergeableSelected,
  otherSelected,
}: EmailPrintDialogProps) {
  const [to, setTo] = useState('')
  const [subject, setSubject] = useState(model.title || '')
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const recipients = parseRecipients(to)
  const attachmentIds = [...mergeableSelected, ...otherSelected].map((a) => a.id)

  const recap = (() => {
    const parts: string[] = []
    parts.push(
      mergeableSelected.length > 0
        ? `1 merged PDF (form + ${mergeableSelected.length} file(s))`
        : '1 PDF (form only)',
    )
    if (otherSelected.length > 0) {
      parts.push(
        `${otherSelected.length} separate file(s): ${otherSelected.map((a) => a.file_name).join(', ')}`,
      )
    }
    return parts.join(' + ')
  })()

  const handleSend = async () => {
    setError(null)
    if (recipients.length === 0) {
      setError('Enter at least one recipient.')
      return
    }
    setSending(true)
    try {
      await printFormsApi.sendEmail({
        connection_id: connectionId,
        model,
        attachment_ids: attachmentIds,
        to: recipients,
        subject,
        body,
      })
      onClose()
    } catch (e) {
      setError(printErrorMessage(e, 'Failed to send email'))
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send by email</DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="To"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="name@example.com, name2@example.com"
            fullWidth
            size="small"
            autoFocus
          />
          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            size="small"
          />
          <Typography variant="caption" color="text.secondary">
            Will send: {recap}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Cancel</Button>
        <Button variant="contained" onClick={handleSend} disabled={sending}>
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
